import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { create } from "zustand";

const DEFAULT_STORAGE_KEY = "spuerhund:split-ratio";
const MIN_SIZE_PX = 100;
const DEFAULT_RATIO = 0.5;
const DIVIDER_PX = 4;

function loadRatio(key: string): number {
  try {
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const val = parseFloat(stored);
      if (Number.isFinite(val) && val > 0 && val < 1) return val;
    }
  } catch {
    // ignore
  }
  return DEFAULT_RATIO;
}

function saveRatio(key: string, ratio: number) {
  try {
    localStorage.setItem(key, ratio.toFixed(4));
  } catch {
    // ignore
  }
}

// Shared store so all SplitPanes with the same key stay in sync
interface SplitRatioState {
  ratios: Record<string, number>;
  dragging: Record<string, boolean>;
  setRatio: (key: string, ratio: number) => void;
  setDragging: (key: string, d: boolean) => void;
}

const useSplitRatioStore = create<SplitRatioState>((set) => ({
  ratios: {},
  dragging: {},
  setRatio: (key, ratio) =>
    set((s) => ({ ratios: { ...s.ratios, [key]: ratio } })),
  setDragging: (key, d) =>
    set((s) => ({ dragging: { ...s.dragging, [key]: d } })),
}));

function useSharedRatio(key: string): number {
  const stored = useSplitRatioStore((s) => s.ratios[key]);
  if (stored !== undefined) return stored;
  // Lazy-init from localStorage on first access
  const loaded = loadRatio(key);
  // Set without re-render loop — will be picked up next render
  useSplitRatioStore.getState().setRatio(key, loaded);
  return loaded;
}

export function SplitPane({
  direction = "vertical",
  storageKey = DEFAULT_STORAGE_KEY,
  children,
}: {
  direction?: "vertical" | "horizontal";
  storageKey?: string;
  children: [ReactNode, ReactNode];
}) {
  const isVertical = direction === "vertical";
  const containerRef = useRef<HTMLDivElement>(null);
  const ratio = useSharedRatio(storageKey);
  const dragging = useSplitRatioStore((s) => !!s.dragging[storageKey]);
  const setRatio = useSplitRatioStore((s) => s.setRatio);
  const setDragging = useSplitRatioStore((s) => s.setDragging);
  const ratioRef = useRef(ratio);

  useEffect(() => {
    ratioRef.current = ratio;
  }, [ratio]);

  const clampRatio = useCallback(
    (r: number, containerSize: number): number => {
      const minRatio = MIN_SIZE_PX / containerSize;
      const maxRatio = 1 - MIN_SIZE_PX / containerSize;
      return Math.min(maxRatio, Math.max(minRatio, r));
    },
    [],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setDragging(storageKey, true);
    },
    [storageKey, setDragging],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const containerSize = isVertical ? rect.height : rect.width;
      if (containerSize <= 0) return;

      const pos = isVertical
        ? e.clientY - rect.top
        : e.clientX - rect.left;

      const newRatio = clampRatio(pos / containerSize, containerSize);
      setRatio(storageKey, newRatio);
    },
    [dragging, isVertical, clampRatio, storageKey, setRatio],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setDragging(storageKey, false);
      saveRatio(storageKey, ratioRef.current);
    },
    [storageKey, setDragging],
  );

  const firstStyle: React.CSSProperties = isVertical
    ? { height: `calc(${ratio * 100}% - ${DIVIDER_PX / 2}px)` }
    : { width: `calc(${ratio * 100}% - ${DIVIDER_PX / 2}px)` };

  const secondStyle: React.CSSProperties = isVertical
    ? { height: `calc(${(1 - ratio) * 100}% - ${DIVIDER_PX / 2}px)` }
    : { width: `calc(${(1 - ratio) * 100}% - ${DIVIDER_PX / 2}px)` };

  return (
    <div
      ref={containerRef}
      className="flex flex-1 overflow-hidden"
      style={{
        flexDirection: isVertical ? "column" : "row",
        userSelect: dragging ? "none" : undefined,
      }}
    >
      <div style={{ ...firstStyle, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {children[0]}
      </div>

      {/* Divider */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          flexShrink: 0,
          [isVertical ? "height" : "width"]: DIVIDER_PX,
          cursor: isVertical ? "row-resize" : "col-resize",
          background: dragging ? "var(--color-divider-active)" : "var(--color-divider)",
          transition: dragging ? "none" : "background 150ms",
          touchAction: "none",
        }}
        onMouseEnter={(e) => {
          if (!dragging) (e.currentTarget.style.background = "var(--color-divider-hover)");
        }}
        onMouseLeave={(e) => {
          if (!dragging) (e.currentTarget.style.background = "var(--color-divider)");
        }}
      />

      <div style={{ ...secondStyle, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {children[1]}
      </div>
    </div>
  );
}
